/**
 * sentry-report.js: first-party defect reporter for storefront analytics code
 * ============================================================================
 *
 * Builds one Sentry event, wraps it in an envelope and POSTs it to the `storefront` project's
 * ingest endpoint. No SDK: the browser bundle is 28 KB gzip and brings global error capture,
 * breadcrumbs and replay that this theme does not want (design: docs/superpowers/specs/
 * 2026-10-14-storefront-sentry-tracking-defects-design.md). Event contract: spec section 4.
 *
 * Only link-tracking.js calls this today. It reports contract violations (TrackingError) and
 * anything else the click handler catches. One Sentry issue per TrackingError code: the
 * fingerprint is ["TrackingError", code]. A missing_event_parameters error lists its missing
 * parameters in the missing_event_parameters tag, joined with "," and never part of the
 * fingerprint. The payload, the parameter list and the hints arrive in extra from err.context.
 * Rules, in order:
 *  - theme editor (Shopify.designMode): never report
 *  - no DSN configured: never send; console.warn in debug mode
 *  - each (tracking_code, missing_event_parameters list, section_alias) once per page view; unexpected errors at most 3 times
 *  - fetch with keepalive so the request outlives the navigation the click starts
 *  - stack frames only for unexpected errors, parsed from err.stack, oldest first; the frames
 *    carry the asset URL as `abs_path` so Sentry can apply Shopify's source map, and
 *    `assets/<file>` as `filename` so the GitHub code mapping resolves them
 *  - every URL in the event (page and link) has account, order and gift card token paths redacted and
 *    userinfo (user:pass@) dropped
 *  - report() never throws into its caller
 */
(function () {
  // Public DSN of the `storefront` Sentry project (public by design, like the GA4 measurement
  // id in link-tracking.js). Empty means "report nothing".
  let DSN =
    "https://a85555e81ecf0100653bd5a465e2e2e7@o4511037961469952.ingest.us.sentry.io/4512268024610816";

  const SDK = { name: "highpass.storefront-reporter", version: "1.0.0" };
  const MAX_UNEXPECTED_PER_PAGE = 3;
  const STACK_LIMIT = 2000;
  const MAX_FRAMES = 50;

  const seen = new Set();
  let unexpectedCount = 0;

  function parseDsn(dsn) {
    const m = /^https:\/\/([a-f0-9]+)@([^/]+)\/(\d+)$/.exec(dsn || "");
    if (!m) return null;
    const key = m[1];
    const host = m[2];
    const projectId = m[3];
    return {
      key: key,
      host: host,
      projectId: projectId,
      endpoint:
        "https://" +
        host +
        "/api/" +
        projectId +
        "/envelope/?sentry_key=" +
        key +
        "&sentry_version=7&sentry_client=" +
        SDK.name +
        "/" +
        SDK.version,
    };
  }

  function eventId() {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    let out = "";
    for (let i = 0; i < bytes.length; i++)
      out += (bytes[i] + 256).toString(16).slice(1);
    return out;
  }

  function theme() {
    const t = (window.Shopify && window.Shopify.theme) || {};
    return {
      id: t.id != null ? String(t.id) : null,
      role: t.role || null,
      version: t.schema_version || null,
    };
  }

  function compact(obj) {
    const out = {};
    for (const key of Object.keys(obj || {})) {
      if (obj[key] != null && obj[key] !== "") out[key] = String(obj[key]);
    }
    return out;
  }

  function isTrackingError(err) {
    return (
      !!err && err.name === "TrackingError" && typeof err.code === "string"
    );
  }

  // The parameters a missing_event_parameters error lists, joined with commas
  // ("" for any other error). Used for the tag and the dedupe key.
  function missingList(err) {
    return isTrackingError(err) &&
      Array.isArray(err.missing_event_parameters) &&
      err.missing_event_parameters.length > 0
      ? err.missing_event_parameters.join(",")
      : "";
  }

  // Chrome/V8: "    at fn (url:12:34)" or "    at url:12:34". Firefox/Safari: "fn@url:12:34".
  const V8_FRAME = /^\s*at (?:(.*?) \()?(?:async )?(.+?):(\d+):(\d+)\)?\s*$/;
  const GECKO_FRAME = /^(.*?)@(.+?):(\d+):(\d+)\s*$/;
  const THEME_ASSET = /\/assets\/([^/?#]+)(?:[?#]|$)/;

  // Turn an err.stack string into Sentry frames, oldest call first. Lines that are not frames
  // (the V8 header, "fn@[native code]") are skipped; a stack with no frames gives [].
  function parseStack(stack) {
    const frames = [];
    const rows = String(stack || "").split("\n");
    for (let i = 0; i < rows.length && frames.length < MAX_FRAMES; i++) {
      const m = V8_FRAME.exec(rows[i]) || GECKO_FRAME.exec(rows[i]);
      if (!m) continue;
      const asset = THEME_ASSET.exec(m[2]);
      frames.push({
        filename: asset ? "assets/" + asset[1] : m[2].split(/[?#]/)[0],
        abs_path: m[2],
        function: (m[1] || "").replace(/^async /, "") || "?",
        lineno: Number(m[3]),
        colno: Number(m[4]),
        in_app: !!asset,
      });
    }
    return frames.reverse();
  }

  // Customer account reset and activation links carry a token in the path
  // (/account/reset/<id>/<token>), order status links end in one (/<shop id>/orders/<token>), and
  // the gift card page is /gift_cards/<id>/<token>.
  function redactPath(path) {
    return path
      .replace(/\/account\/(reset|activate)\/.*$/, "/account/$1/[redacted]")
      .replace(/\/orders\/.*$/, "/orders/[redacted]")
      .replace(/\/gift_cards\/.*$/, "/gift_cards/[redacted]");
  }

  // Redact token paths in a URL and leave everything else (origin, query string, hash) as given.
  // Accepts an absolute URL, a protocol-relative one or a bare path. Anything that is not a
  // string is returned unchanged; this never throws.
  function scrubUrl(value) {
    if (typeof value !== "string") return value;
    const cut = value.search(/[?#]/);
    const head = cut === -1 ? value : value.slice(0, cut);
    const tail = cut === -1 ? "" : value.slice(cut);
    const m = /^((?:[a-z][a-z0-9+.-]*:)?\/\/)([^/]*)(.*)$/i.exec(head);
    if (m) return m[1] + m[2].replace(/^.*@/, "") + redactPath(m[3]) + tail;
    return redactPath(head) + tail;
  }

  // The page's own pathname, with token paths redacted (a pathname has no query string).
  function pagePath() {
    return scrubUrl(String(window.location.pathname || ""));
  }

  function buildEvent(err, opts) {
    opts = opts || {};
    const t = theme();
    const path = pagePath();
    const tracking = isTrackingError(err);
    // Only a missing_event_parameters error carries a list. It becomes a tag, never part of
    // the fingerprint: every combination of a code shares one Sentry issue.
    const missing = missingList(err);
    const name = (err && err.name) || "Error";
    const message =
      err && err.message != null ? String(err.message) : String(err);
    const event = {
      event_id: eventId(),
      timestamp: Date.now() / 1000,
      platform: "javascript",
      level: "error",
      logger: "link-tracking",
      environment: t.role === "main" ? "production" : "preview",
      transaction: path,
      request: { url: scrubUrl(window.location.href) },
      sdk: SDK,
      tags: compact(
        Object.assign(
          { theme_id: t.id, theme_role: t.role },
          tracking ? { tracking_code: err.code } : {},
          opts.tags || {},
          missing ? { missing_event_parameters: missing } : {},
        ),
      ),
      extra: Object.assign(
        {},
        tracking
          ? err.context || {}
          : {
              stack:
                err && err.stack
                  ? String(err.stack).slice(0, STACK_LIMIT)
                  : null,
            },
        opts.extra || {},
      ),
      fingerprint: tracking
        ? ["TrackingError", err.code]
        : ["link-tracking-handler", name],
      exception: {
        values: [
          {
            type: name,
            value: tracking ? err.code + ": " + message : message,
            mechanism: { type: "generic", handled: true },
          },
        ],
      },
    };
    // extra was copied above, so scrubbing it leaves the caller's objects alone.
    const extra = event.extra;
    if (typeof extra.href === "string") extra.href = scrubUrl(extra.href);
    const payload = extra.payload;
    if (
      payload &&
      typeof payload === "object" &&
      typeof payload.link_url === "string"
    ) {
      extra.payload = Object.assign({}, payload, {
        link_url: scrubUrl(payload.link_url),
      });
    }
    if (!tracking) {
      const frames = parseStack(err && err.stack);
      if (frames.length)
        event.exception.values[0].stacktrace = { frames: frames };
    }
    if (t.version) event.release = "highpass-theme@" + t.version;
    return event;
  }

  function buildEnvelope(event, dsn) {
    const header = {
      event_id: event.event_id,
      sent_at: new Date().toISOString(),
      dsn: dsn,
      sdk: SDK,
    };
    return [
      JSON.stringify(header),
      JSON.stringify({ type: "event" }),
      JSON.stringify(event),
    ].join("\n");
  }

  function send(endpoint, body) {
    try {
      const p = window.fetch(endpoint, {
        method: "POST",
        mode: "cors",
        credentials: "omit",
        keepalive: true,
        headers: { "Content-Type": "text/plain;charset=UTF-8" },
        body: body,
      });
      if (p && typeof p.catch === "function") p.catch(function () {});
      return true;
    } catch (e) {
      return false;
    }
  }

  function report(err, opts) {
    opts = opts || {};
    try {
      if (window.Shopify && window.Shopify.designMode) return false;
      const tracking = isTrackingError(err);
      if (tracking) {
        const alias = (opts.tags && opts.tags.section_alias) || "";
        const key = err.code + "|" + missingList(err) + "|" + alias;
        if (seen.has(key)) return false;
        seen.add(key);
      } else {
        if (unexpectedCount >= MAX_UNEXPECTED_PER_PAGE) return false;
        unexpectedCount++;
      }
      const dsn = parseDsn(DSN);
      const event = buildEvent(err, opts);
      if (!dsn) {
        if (opts.debug)
          console.warn(
            "[sentry-report] no DSN configured; would report",
            event,
          );
        return false;
      }
      return send(dsn.endpoint, buildEnvelope(event, DSN));
    } catch (e) {
      if (opts.debug) console.warn("[sentry-report] report failed", e);
      return false;
    }
  }

  window.HighpassSentry = {
    report: report,
    buildEvent: buildEvent,
    buildEnvelope: buildEnvelope,
    parseDsn: parseDsn,
    parseStack: parseStack,
    scrubUrl: scrubUrl,
    configure: function (c) {
      DSN = (c && c.dsn) || "";
    },
    isConfigured: function () {
      return parseDsn(DSN) !== null;
    },
    _resetForTests: function () {
      seen.clear();
      unexpectedCount = 0;
    },
    _dsnForTests: function () {
      return DSN;
    },
  };
})();
